import React, { useRef, useState, useEffect } from 'react';
import './MysticaReplica.css'; // Reusing the same CSS
import Carousel, { CarouselItem } from '../blocks/Carousel/Carousel';
import AnimatedList from '../blocks/AnimatedList/AnimatedList';
import MetaBalls from '../blocks/MetaBalls/MetaBalls';
import SpotlightCard from '../blocks/SpotlightCard/SpotlightCard';
import ClickSpark from '../blocks/ClickSpark/ClickSpark';
import BlurText from '../blocks/BlurText/BlurText';
import { CSSProperties } from 'react';
import { detectBrowserLanguage, getTranslations, Language } from "../utils/localization";

// Custom styles for the Amir page
const titleStyle: CSSProperties = {
  fontFamily: 'Cinzel, serif',
  fontWeight: 700,
  fontSize: '7rem',
  letterSpacing: '0.05em',
  color: 'var(--text-color)',
  textTransform: 'uppercase',
  margin: 0,
  padding: 0,
  lineHeight: 1.2,
  display: 'inline-block',
  width: '100%',
  textAlign: 'center'
};

const subtitleStyle: CSSProperties = {
  fontSize: '2.5rem',
  fontWeight: 400,
  lineHeight: 1.4,
  maxWidth: '1600px',
  margin: '2rem auto',
  height: '150px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const languageSwitcherStyle: CSSProperties = {
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1000,
  display: 'flex',
  borderRadius: '8px',
  overflow: 'hidden'
};

const Amir: React.FC = () => {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [language, setLanguage] = useState<Language>('en');
  const [translations, setTranslations] = useState(getTranslations('en').amir);

  // Detect browser language on component mount
  useEffect(() => {
    const detectedLanguage = detectBrowserLanguage();
    setLanguage(detectedLanguage);
    setTranslations(getTranslations(detectedLanguage).amir);
  }, []);

  // Toggle language function
  const toggleLanguage = (lang: Language) => {
    setLanguage(lang);
    setTranslations(getTranslations(lang).amir);
  };

  const sectionRefs = {
    behind: useRef<HTMLElement>(null),
    showcase: useRef<HTMLElement>(null),
    gameplay: useRef<HTMLElement>(null),
    narrative: useRef<HTMLElement>(null),
    challenges: useRef<HTMLElement>(null)
  };

  const [activeSection, setActiveSection] = useState<number>(0);

  const scrollToSection = (item: string, index: number) => {
    const refKeys = Object.keys(sectionRefs);
    const ref = sectionRefs[refKeys[index] as keyof typeof sectionRefs];
    
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(index);
    }
  };

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = Object.values(sectionRefs);
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // Set a minimum threshold of visibility for a section to be considered active
      // This helps with smaller sections
      const visibilityThreshold = 100; // px from top of viewport
      
      // Check each section
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i].current;
        if (!section) continue;
        
        const rect = section.getBoundingClientRect();
        
        // A section is active if its top is within the threshold from the viewport top
        // OR if we're within the section (between its top and bottom)
        if (
          (rect.top >= 0 && rect.top <= visibilityThreshold) ||
          (rect.top <= 0 && rect.bottom > visibilityThreshold)
        ) {
          setActiveSection(i);
          return;
        }
      }
      
      // If we get here, no section matches our primary criteria
      // Use a fallback approach based on which section occupies most of the viewport
      let maxVisibleSection = 0;
      let maxVisibleHeight = 0;
      
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i].current;
        if (!section) continue;
        
        const rect = section.getBoundingClientRect();
        
        // Calculate how much of the section is visible in the viewport
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(viewportHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        
        if (visibleHeight > maxVisibleHeight) {
          maxVisibleHeight = visibleHeight;
          maxVisibleSection = i;
        }
      }
      
      setActiveSection(maxVisibleSection);
    };
    
    // Throttle the scroll handler to improve performance and reduce sensitivity
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const throttledScrollHandler = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 50); // 50ms delay
    };
    
    window.addEventListener('scroll', throttledScrollHandler);
    handleScroll(); // Initial call
    
    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  // State to track collisions between side navigation and content
  const [sideNavVisible, setSideNavVisible] = useState(true);
  const sideNavRef = useRef<HTMLDivElement>(null);
  const collisionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check for collisions between side navigation and each section
  useEffect(() => {
    const checkCollision = () => {
      if (!sideNavRef.current) return;
      
      const sideNavRect = sideNavRef.current.getBoundingClientRect();
      let hasCollision = false;
      
      // Add a small margin to detect collision before actual overlap
      const collisionMargin = 20; // 20px margin
      
      // Check collision with each section
      Object.values(sectionRefs).forEach(sectionRef => {
        if (!sectionRef.current) return;
        
        const sectionRect = sectionRef.current.getBoundingClientRect();
        
        // Calculate horizontal overlap with margin
        const horizontalOverlap = !(
          sideNavRect.right < sectionRect.left - collisionMargin || 
          sideNavRect.left > sectionRect.right + collisionMargin
        );
        
        // Calculate vertical overlap (only consider sections in viewport)
        const verticalOverlap = !(
          sectionRect.bottom < 0 ||
          sectionRect.top > window.innerHeight
        );
        
        // If there's both horizontal and vertical overlap, we have a collision
        if (horizontalOverlap && verticalOverlap) {
          hasCollision = true;
        }
      });
      
      setSideNavVisible(!hasCollision);
    };
    
    // Debounced check collision
    const debouncedCheckCollision = () => {
      if (collisionTimeoutRef.current) {
        clearTimeout(collisionTimeoutRef.current);
      }
      
      collisionTimeoutRef.current = setTimeout(checkCollision, 100);
    };
    
    window.addEventListener('scroll', debouncedCheckCollision);
    window.addEventListener('resize', debouncedCheckCollision);
    
    // Initial check
    checkCollision();
    
    return () => {
      window.removeEventListener('scroll', debouncedCheckCollision);
      window.removeEventListener('resize', debouncedCheckCollision);
      
      if (collisionTimeoutRef.current) {
        clearTimeout(collisionTimeoutRef.current);
      }
    };
  }, []);

  const showcaseItems: CarouselItem[] = [
    {
      id: 1,
      title: translations.showcase.items.townScenes.title,
      description: translations.showcase.items.townScenes.description,
      image: "/images/projects/amir/showCase1.png",
      alt: translations.showcase.items.townScenes.title
    },
    {
      id: 2,
      title: translations.showcase.items.narrativeBox.title,
      description: translations.showcase.items.narrativeBox.description,
      image: "/images/projects/amir/showCase2.png",
      alt: translations.showcase.items.narrativeBox.title
    },
    {
      id: 3,
      title: translations.showcase.items.tradingSystem.title,
      description: translations.showcase.items.tradingSystem.description,
      image: "/images/projects/amir/showCase3.png",
      alt: translations.showcase.items.tradingSystem.title
    },
    {
      id: 4,
      title: translations.showcase.items.combatSystem.title,
      description: translations.showcase.items.combatSystem.description,
      image: "/images/projects/amir/showCase4.png",
      alt: translations.showcase.items.combatSystem.title
    }
  ];

  return (
<ClickSpark
                shape="heart"
                sparkColor='#BD2735'
                sparkSize={8}
                sparkRadius={40}
                sparkCount={16}
                duration={500}
              >      <div className="project-page mystica-page">
        <div className="home-link">
          <div className="metaball-wrapper">
            <MetaBalls 
              color="#266867" 
              enableTransparency={true}
              ballCount={8}
              animationSize={25}
              speed={0.2}
              cursorBallColor="#266867"
            />
          </div>
          <a href="/">Home</a>
        </div>

        {/* Language Switcher */}
        <div style={languageSwitcherStyle}>
          <button 
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => toggleLanguage('en')}
          >
            EN
          </button>
          <button 
            className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
            onClick={() => toggleLanguage('zh')}
          >
            中文
          </button>
        </div>

        <header className="project-header">
          <div className="container">
            <h1 style={titleStyle}>
              <a 
                href="https://bellinimoon.itch.io/amir" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <BlurText 
                  text={translations.title}
                  delay={150}
                  animateBy="letters"
                  direction="top"
                  className="title-container"
                  style={{fontSize: '7rem', lineHeight: 1.2}}
                />
              </a>
            </h1>
            <hr />
            <h2 style={subtitleStyle}>
              <BlurText 
                text={translations.subtitle}
                delay={100}
                animateBy="words"
                direction="bottom"
                className="description-container"
                style={{fontSize: '2.5rem', lineHeight: 1.4}}
              />
            </h2>
          </div>
        </header>

        <main className="project-content">
          <div className="container">
            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.behind}>
                <h3>{translations.navItems[0]}</h3>
                <p>
                  {translations.sections.behindTheScene}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section showcase-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.showcase}>
                <h3>{translations.showcase.title}</h3>
                <div className="carousel-wrapper">
                  <Carousel 
                    items={showcaseItems}
                    baseWidth={800}
                    autoplay={true}
                    autoplayDelay={5000}
                    pauseOnHover={true}
                    loop={true}
                  />
                </div>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.gameplay}>
                <h3>{translations.navItems[2]}</h3>
                <p>
                  {translations.sections.gameplay}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.narrative}>
                <h3>{translations.navItems[3]}</h3>
                <p>
                  {translations.sections.narrative}
                </p>
              </section>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <section ref={sectionRefs.challenges}>
                <h3>{translations.navItems[4]}</h3>
                <p>
                  {translations.sections.challenges}
                </p>
              </section>
            </SpotlightCard>
          </div>
        </main>

        <div 
          className="side-navigation" 
          ref={sideNavRef} 
          style={{ 
            opacity: sideNavVisible ? 1 : 0,
            visibility: sideNavVisible ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
          }}
        >
          <AnimatedList
            items={translations.navItems}
            onItemSelect={(item, index) => {
              scrollToSection(item, index);
              setActiveSection(index);
            }}
            showGradients={false}
            enableArrowNavigation={true}
            className="mystica-nav"
            displayScrollbar={false}
            selectedIndex={activeSection}
          />
        </div>
      </div>
    </ClickSpark>
  );
};

export default Amir;